/** @jsxImportSource theme-ui */
import { Flex, Text, Spinner, Box, Divider, Heading, Link, get } from 'theme-ui';
import { useEffect } from 'react';
import { Table } from '@component-controls/components';
import { JSONTree } from 'react-json-tree';
import { useDialogState, Dialog, DialogBackdrop } from 'reakit/Dialog';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import qs from 'qs';
import { format } from 'date-fns';
import {
    useLazyGetVossInvoicesQuery,
    useLazyGetVossAsyncOperationsQuery,
} from '@/store/apiService';
import jsonTreeTheme from '@/theme/jsonTreeTheme';
import Button from '@/components/Button';
import { getErrorMessage } from '@/utils/helpers';
import { theme } from '@/theme';

type Props = {};

const invoicesColumns = [
    {
        columnKey: 'id',
        Header: 'Id',
        accessor: 'id',
    },
    {
        columnKey: 'status',
        Header: 'Invoice status',
        accessor: 'status',
    },
    {
        columnKey: 'invoiceDate',
        Header: 'Invoice date',
        accessor: 'invoiceDate',
        Cell: ({ cell }) => {
            if (!cell.value) return '';
            return format(new Date(cell.value), 'd MMM yyyy, p');
        },
    },
    {
        columnKey: 'totalAmount',
        Header: 'Total Amount',
        accessor: 'totalAmount',
        width: '50px',
    },
    {
        columnKey: 'filters',
        Header: 'Filters',
        accessor: 'filters',
        Cell: ({ row, cell }) => {
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={cell.value}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            return true;
                        }}
                    />
                </Box>
            );
        },
    },
];

const asyncOperationsColumns = [
    {
        columnKey: 'id',
        Header: 'Id',
        accessor: 'id',
    },
    {
        columnKey: 'status',
        Header: 'Invoice status',
        accessor: 'status',
    },
    {
        columnKey: 'started',
        Header: 'Started',
        accessor: 'started',
        Cell: ({ cell }) => {
            if (!cell.value) return '';
            return format(new Date(cell.value), 'd MMM yyyy, p');
        },
    },
    {
        columnKey: 'completed',
        Header: 'Completed',
        accessor: 'completed',
        Cell: ({ cell }) => {
            if (!cell.value) return '';
            return format(new Date(cell.value), 'd MMM yyyy, p');
        },
    },
    {
        columnKey: 'resourceLocation',
        Header: () => <Flex sx={{ justifyContent: 'center' }}>Resource location</Flex>,
        accessor: 'resourceLocation',
        Cell: ({ cell }) => <ResourceLocation url={cell.value} />,
    },
    {
        columnKey: 'type',
        Header: 'Type',
        accessor: 'type',
    },
];

const ResourceLocation: React.FC<{ url: string }> = ({ url }) => {
    const [getVossInvoice, vossInvoiceState] = useLazyGetVossInvoicesQuery();
    const dialog = useDialogState();
    const invoiceId = url?.split('/').pop();

    useEffect(() => {
        if (vossInvoiceState.data && !vossInvoiceState.isFetching) {
            dialog.show();
        }

        if (vossInvoiceState.error) {
            toast.error(getErrorMessage(vossInvoiceState.error));
        }
    }, [vossInvoiceState.fulfilledTimeStamp, vossInvoiceState.isSuccess, vossInvoiceState.error]);

    return (
        <>
            <DialogBackdrop {...dialog}>
                <Dialog
                    sx={{
                        width: '2/3',
                        maxWidth: '640px',
                        '&&': {
                            padding: 4,
                            minHeight: '480px',
                            overflow: 'scroll',
                        },
                    }}
                    {...dialog}
                >
                    <Box
                        sx={{
                            '& > ul': {
                                padding: (t: any) => `${t.space[2]} !important`,
                                borderRadius: (t: any) => `${t.radii['sm']} !important`,
                            },
                        }}
                    >
                        <JSONTree
                            data={vossInvoiceState.data}
                            hideRoot={true}
                            theme={jsonTreeTheme}
                            invertTheme={true}
                            shouldExpandNodeInitially={(keyPath, data, level) => {
                                return true;
                            }}
                        />
                    </Box>
                </Dialog>
            </DialogBackdrop>
            <Button
                variant="plain"
                onClick={() => {
                    getVossInvoice({ InvoiceId: invoiceId });
                }}
                isLoading={vossInvoiceState.isFetching}
            >
                {url}
            </Button>
        </>
    );
};

const VossInvoices = (props: Props) => {
    const [searchParams] = useSearchParams();
    const [getVossInvoices, vossInvoicesState] = useLazyGetVossInvoicesQuery();
    const [getAsyncOperations, asyncOperationsState] = useLazyGetVossAsyncOperationsQuery();

    const vossInvoices = vossInvoicesState.data ? vossInvoicesState.data : [];

    const asyncOperations = asyncOperationsState.data ? asyncOperationsState.data : [];

    useEffect(() => {
        getVossInvoices({});
        getAsyncOperations({});
    }, []);

    return (
        <Flex sx={{ flexDirection: 'column', gap: 2 }}>
            <Heading>Invoices</Heading>
            <Flex>
                {vossInvoices.length > 0 ? (
                    <Table
                        header={true}
                        sorting={true}
                        filtering={true}
                        columns={invoicesColumns}
                        data={vossInvoices}
                    />
                ) : vossInvoicesState.isSuccess ? (
                    <Text>No voss invoices found</Text>
                ) : null}
            </Flex>

            {vossInvoicesState.isFetching ? (
                <Flex sx={{ justifyContent: 'center' }}>
                    <Spinner size={16} />
                </Flex>
            ) : null}

            <Divider />

            <Heading>Async operations</Heading>
            <Flex>
                {asyncOperations.length > 0 ? (
                    <Table
                        header={true}
                        sorting={true}
                        filtering={true}
                        columns={asyncOperationsColumns}
                        data={asyncOperations}
                    />
                ) : asyncOperationsState.isSuccess ? (
                    <Text>No async operations found</Text>
                ) : null}
            </Flex>

            {asyncOperationsState.isFetching ? (
                <Flex sx={{ justifyContent: 'center' }}>
                    <Spinner size={16} />
                </Flex>
            ) : null}
        </Flex>
    );
};

export default VossInvoices;
